import React, { useEffect, useState, useRef } from "react";
import { T } from "@/lib/copy";
import { api } from "@/lib/api";
import { playCall } from "@/lib/audio";
import { Loader2 } from "lucide-react"; // Bug 3: audio buffering spinner

// REVEAL (V1) — outcome blaze + caption strip (= voice text) + DRS + next ball.
// V1 lock: reveal card (reveal + principle + why) is deferred to V2. Voice auto-plays.
export function RevealView({ res, overId, ballNo, trial, lang, onNext, finishLabel, reviewUsed = false, onReviewUsed, persona }) {
  const t = T(lang);
  const [stage, setStage] = useState(1);
  const [caption, setCaption] = useState(() => res.autocall_text || "");
  const [drs, setDrs] = useState(null);
  const [drsUsedEver] = useState(() => { try { return localStorage.getItem("so_drs_used") === "1"; } catch (e) { return false; } });
  const [audioLoading, setAudioLoading] = useState(false); // Bug 3: buffering indicator
  const playedRef = useRef(false);

  useEffect(() => {
    setStage(1); setDrs(null); setCaption(res.autocall_text || ""); playedRef.current = false;
    if (!playedRef.current) {
      playedRef.current = true;
      const fb = res.autocall_text || { "6": "Six!", "4": "Four!", "1": "Single.", "2": "Two runs.", "0": "Dot ball.", "W": "Out!" }[res.outcome] || "Dot ball.";
      // Bug 3: show spinner until audio resolves; force-clear after 2s so user is never stuck.
      setAudioLoading(true);
      const t2 = setTimeout(() => setAudioLoading(false), 2000);
      playCall({ over_id: overId, ball_no: ballNo, lang, fallbackText: fb, trial }).then((r) => {
        clearTimeout(t2);
        setAudioLoading(false);
        if (r && r.text) setCaption(r.text);
      });
    }
    // eslint-disable-next-line
  }, [overId, ballNo, res.outcome]);

  async function doReview() {
    try { localStorage.setItem("so_drs_used", "1"); } catch (e) { /* noop */ }
    setDrs("loading");
    try { const r = await api.review(overId, ballNo, lang); setDrs(r); if (onReviewUsed) onReviewUsed(); }
    catch (e) { setDrs({ verdict: "stands", text: t.drsFallback, sources: [] }); if (onReviewUsed) onReviewUsed(); }
  }

  const oc = res.outcome;
  const notOut = res.free_hit_note && res.is_wicket;
  let big = "DOT", col = "var(--sm-muted)", sh = "transparent", sub = "", subc = "var(--sm-muted)";
  if (res.is_wicket && !notOut) { big = "OUT"; col = "var(--sm-drs-1)"; sh = "rgba(127,166,206,0.5)"; sub = t.subWicket; subc = "var(--sm-drs-2)"; }
  else if (notOut) { big = "NOT OUT"; col = "var(--sm-blaze)"; sh = "rgba(255,154,61,0.45)"; sub = t.subFreeHit; subc = "#C99B6A"; }
  else if (oc === "6" || oc === "4") { big = oc === "6" ? "SIX!" : "FOUR!"; col = "var(--sm-blaze)"; sh = "rgba(255,154,61,0.6)"; sub = (res.free_hit_note ? res.free_hit_note + " · " : "") + "+" + (res.runs || (oc === "6" ? 6 : 4)) + " · " + t.subGreatCall; subc = "var(--sm-floodlight)"; }
  else if (oc === "0") { big = res.timed_out ? "…DOT" : "DOT"; sub = t.subNoRun; }
  else { big = (res.runs === 1 || oc === "1") ? "SINGLE" : (res.runs || 2) + " RUNS"; col = "#E8E3D8"; sh = "rgba(232,227,216,0.3)"; sub = "+" + (res.runs || (oc === "2" ? 2 : 1)) + " · " + t.subRotate; subc = "#9099AA"; }
  if (trial) {
    col = res.is_wicket ? "var(--sm-drs-1)" : (oc === "6" || oc === "4") ? "var(--sm-blaze)" : "#E8E3D8";
    sub = res.is_wicket ? t.subTrialWicket : (oc === "6" || oc === "4") ? t.subTrialGood : t.subTrialEye;
    subc = "var(--sm-drs-1)";
  }

  return (
    <div className="dreveal" data-testid="reveal-screen">
      <div className="outcome-wrap">
        <div className="outcome-big" style={{ color: col, textShadow: `0 0 40px ${sh}` }} data-testid="reveal-outcome-blaze">{big}</div>
        <div className="outcome-sub" style={{ color: subc }}>{sub}</div>
        {!trial && res.seat && res.seat.text && (
          <div className="font-mono up" data-testid="reveal-seat-line"
            style={{ fontSize: 9.5, letterSpacing: "0.14em", marginTop: 8, color: "rgba(244,239,230,0.55)" }}>
            {res.seat.text}
          </div>
        )}
      </div>

      {stage >= 1 && (
        <div className="flex flex-col gap-3">
          {caption && (
            <div className="voice-block anim-fade" data-testid="reveal-caption-strip"
              style={{ padding: "14px 16px", borderRadius: 12, background: "rgba(244,239,230,0.05)", border: "1px solid rgba(244,239,230,0.08)" }}>
              <div className="font-reading" style={{ fontSize: 15, lineHeight: 1.55, color: "var(--sm-chalk)" }}>{caption}</div>
              <div className="sign" style={{ marginTop: 8, fontSize: 9.5, letterSpacing: "0.16em", opacity: 0.55, display: "flex", alignItems: "center", gap: 6 }}>
                — THE MAIDAAN
                {audioLoading && <Loader2 size={11} className="animate-spin" data-testid="reveal-audio-loading" style={{ opacity: 0.75 }} />}
              </div>
            </div>
          )}

          {!trial && (
            drs == null ? (
              reviewUsed ? (
                <div className="review-used" data-testid="reveal-review-used">{t.reviewUsed}</div>
              ) : (
                <button className={"review-btn" + (drsUsedEver ? "" : " invite")} onClick={doReview} data-testid="reveal-review-button">
                  <span className="review-t">T</span>{t.review} · 1 {t.reviewLeft}
                </button>
              )
            ) : drs === "loading" ? (
              <div className="review-used" data-testid="reveal-drs-loading">{t.maidaanChecking}</div>
            ) : (
              <div className="drs-panel" data-testid="reveal-drs-panel">
                <div className="drs-label">{t.drsPanelLabel}</div>
                <div className="drs-text">{drs.text}</div>
                {drs.sources && drs.sources.length > 0 && (
                  <div className="drs-label" style={{ marginTop: 6, color: "rgba(159,192,224,0.6)" }}>SOURCE · {drs.sources.join(" · ")}</div>
                )}
              </div>
            )
          )}

          <button className="btn-primary" style={{ width: "100%", marginTop: 4, padding: 17, fontSize: 18 }} onClick={onNext} data-testid="reveal-next-ball-button">
            {finishLabel || t.nextBall}
          </button>
        </div>
      )}
    </div>
  );
}
